import React from "react";

import { Alert, AlertTitle, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { ErrorType } from "@/types";


interface ErrorPanelProps {
  errors: Partial<Record<ErrorType, boolean | string | null>>;
}

const ErrorPanel: React.FC<ErrorPanelProps> = ({
  errors
}) => {


  const ERROR_MESSAGES: Record<ErrorType, {
    severity: "success" | "error" | "info" | "warning" | undefined,
    title?: string;
    message: string
  }> = {
    AudiencesLoadError: {
      severity: 'error',
      title: "Unable to load Audiences",
      message: `
        It was not possible to load your Audiences at this time.
        Please try again and contact the support team if the problem persists.
      `
    },
    UploadsLoadError: {
      severity: 'error',
      title: "Unable to load Uploads",
      message: `
        It was not possible to load your Uploads at this time.
        Please try again and contact the support team if the problem persists.
      `
    },
    ActivationsLoadError: {
      severity: 'error',
      title: "Unable to load Activations",
      message: `
        It was not possible to load your Activations at this time.
        Please try again and contact the support team if the problem persists.
      `
    },
    AudienceNotFound: {
      severity: 'error',
      title: "Audience Not Found",
      message: `
        Unable to load Audience, either the Audience ID is incorrect or you do not have permissions to view this Audience. 
        Please check that the Audience ID is correct and try again.
      `
    },
    UploadNotFound: {
      severity: 'error',
      title: "Upload Not Found",
      message: `
        Unable to load Upload, either the Upload ID is incorrect or you do not have permissions to view this Upload. 
        Please check that the Upload ID is correct and try again.
      `
    },
    ActivationNotFound: {
      severity: 'error',
      title: "Activation Not Found",
      message: `
        Unable to load Activation, either the Campaign ID is incorrect or you do not have permissions to view this Activation. 
        Please check that the Campaign ID is correct and try again.
      `
    },
    InsightsNotFound: {
      severity: 'error',
      title: "Insights Not Found",
      message: `
        Unable to load Insights.
        Please rebuild the audience and try again.
      `
    },
    ComparisonInsightsNotFound: {
      severity: 'error',
      title: "Insights Not Found",
      message: `
        Unable to load Insights for this Comparison.
        Please rebuild the audience and try again.
      `
    },
    AudienceError: {
      severity: 'error',
      title: "Audience Error",
      message: `
        There was an error with this audience. 
        Please rebuild the audience and re-create the insights.
      `
    },
    DataUnavailable: {
      severity: 'error',
      title: "Data Error",
      message: `
        There was a problem loading data for this page. 
        Please try again and contact the support team if the problem persists.
      `
    },
    BuildError: {
      severity: 'error',
      title: "Build Error",
      message: `
        Unable to Build audience.
        Please try again and contact the support team if the problem persists.
      `
    },
    InsightError: {
      severity: 'error',
      title: "Insights Error",
      message: `
        Unable to calculate Insights.
        Please try again and contact the support team if the problem persists.
      `
    },
    ExportError: {
      severity: 'error',
      title: "Export Error",
      message: `
        Unable to create Export.
        Please try again and contact the support team if the problem persists.
      `
    },
    InvalidVariables: {
      severity: 'error',
      title: "Legacy Variables Detected",
      message: `
        One or more variables are no longer available. Update them below to re-build the audience.
      `
    },
    NotificationError: {
      severity: 'error',
      title: "Notification Error ",
      message: `
        Could not load notifications.
        Please refresh the page and contact the support team if the problem persists.
      `
    },
    TraveltimeError: {
      severity: 'error',
      title: "Traveltime Error",
      message: `
        There was an error creating the Traveltime Catchment.
        Please re-upload the file and re-create the catchmemt. Contact the support team if the problem persists.
      `
    },
    SiteCatchmentError: {
      severity: 'error',
      title: 'Catchment Error',
      message: `
        There was an error creating the Catchment.
        Please clear the site selection and start again. Contact the support team if the problem persists.
      `
    },
    AudienceProcessing: {
      severity: 'info',
      title: "Audience Processing",
      message: `
        This audience is still processing, please wait. 
        You will receive a notification when the insights are complete.
      `
    },
    AudienceShared: {
      severity: 'info',
      title: "Shared Audience",
      message: `
        You are viewing an audience that has been shared with you
        and therefore any edit functionality has been disabled. You
        may still view the insights for and export this audience. We
        will soon be adding the ability to clone this audience so
        that you can make changes.
      `
    }
  };

  // Get all the error types that have been provided
  const activeErrors = Object.keys(errors).filter(
    (key) => !!errors[key as ErrorType]
  ) as ErrorType[];

  if (activeErrors.length === 0) return null;


  return (
    <Grid size={12}>
      <Stack spacing={1}>
        {activeErrors.map((key) => {
          const config = ERROR_MESSAGES[key];
          const val = errors[key];

          // If the value is a string, use it as the message; otherwise use the registry default
          const displayMessage = typeof val === "string" ? val : config.message;

          return (
            <Alert key={key} variant="filled" severity={config.severity} sx={{ width: '100%' }}>
              {config.title && <AlertTitle>{config.title}</AlertTitle>}
              {displayMessage}
            </Alert>
          );
        })}
      </Stack>
    </Grid>
  );
};

export default ErrorPanel;
